/**
 * Guide — public "how it works" walkthrough.
 *
 * Anchor sidebar (desktop) + progressive sections. Written in French, tuned
 * for Lexia School's editorial voice: precise, warm, non-condescending.
 */
import LegalShell from "@/components/LegalShell";

const SECTIONS = [
    { id: "demarrage", label: "Démarrage" },
    { id: "onboarding", label: "Onboarding et test de niveau" },
    { id: "fonctionnalites", label: "Fonctionnalités" },
    { id: "faq", label: "Questions fréquentes" },
];

function Anchor({ id, title, kicker }) {
    return (
        <div id={id} className="scroll-mt-28">
            {kicker && (
                <p className="text-[11px] tracking-[0.24em] uppercase text-terracotta font-semibold mb-2">
                    {kicker}
                </p>
            )}
            <h2 className="font-serif text-2xl md:text-3xl text-ink">{title}</h2>
        </div>
    );
}

export default function Guide() {
    return (
        <LegalShell
            testid="page-guide"
            eyebrow="Guide d'utilisation"
            title="Comment fonctionne Lexia School."
            subtitle="En 5 minutes, vous avez tout compris. Nous privilégions la régularité et le rappel long terme sur le volume."
        >
            <nav
                aria-label="Sommaire"
                className="rounded-2xl border border-lexiBorder bg-paperAlt p-4"
                data-testid="guide-toc"
            >
                <p className="text-[10px] tracking-[0.24em] uppercase text-inkSoft mb-2">Sommaire</p>
                <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                    {SECTIONS.map((s, i) => (
                        <li key={s.id}>
                            <a
                                href={`#${s.id}`}
                                className="text-ink hover:text-terracotta transition-colors"
                                data-testid={`guide-toc-${s.id}`}
                            >
                                {String(i + 1).padStart(2, "0")} · {s.label}
                            </a>
                        </li>
                    ))}
                </ol>
            </nav>

            {/* Démarrage */}
            <section className="space-y-4">
                <Anchor id="demarrage" kicker="Étape 1" title="Prendre en main l'application" />
                <p>
                    Lexia School est une application web pensée pour apprendre une langue à votre rythme, guidée par
                    une coach vocale nommée Lexia. Aucune installation nécessaire — vous ouvrez la plateforme depuis
                    votre navigateur, sur ordinateur, tablette ou téléphone.
                </p>
                <ol className="list-decimal pl-5 space-y-2">
                    <li>
                        <strong>Créez votre compte</strong> — un email, un mot de passe, votre prénom.
                    </li>
                    <li>
                        <strong>Choisissez votre langue cible</strong> — anglais, espagnol, italien, allemand, portugais,
                        japonais, arabe, russe et une soixantaine d&apos;autres langues.
                    </li>
                    <li>
                        <strong>Indiquez votre objectif</strong> — voyage, professionnel, examen, immigration, culture,
                        ou simplement conversation. Le programme s&apos;adapte.
                    </li>
                    <li>
                        <strong>Fixez votre temps quotidien</strong> — 10, 25, 50 ou 100 mots par jour. Vous pouvez
                        changer à tout moment.
                    </li>
                </ol>
            </section>

            {/* Onboarding */}
            <section className="space-y-4">
                <Anchor id="onboarding" kicker="Étape 2" title="Onboarding et test de niveau" />
                <p>
                    Lors de votre première visite, nous vous proposons un rapide questionnaire suivi d&apos;un{" "}
                    <strong>test de placement calibré CECRL</strong> (compréhension écrite, expression, quelques éléments
                    de grammaire). Il ne dure que quelques minutes.
                </p>
                <p>
                    À l&apos;issue du test, vous êtes positionné·e sur l&apos;échelle CECRL — de <em>débutant</em> à{" "}
                    <em>C2</em>. Ce niveau est <strong>estimatif et non certifiant</strong> : il sert de point de départ,
                    pas de diplôme.
                </p>
            </section>

            {/* Fonctionnalités */}
            <section className="space-y-4">
                <Anchor id="fonctionnalites" kicker="Étape 3" title="Ce que vous avez à disposition" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-lexiBorder bg-white p-5">
                        <h3 className="font-serif text-lg">Leçon du jour</h3>
                        <p className="text-sm text-inkSoft mt-1">
                            Une leçon quotidienne en 10 étapes : introduction, vocabulaire nouveau, phrases naturelles,
                            grammaire ciblée, écoute, compréhension, quiz, révisions, conversation guidée, prononciation.
                        </p>
                    </div>
                    <div className="rounded-2xl border border-lexiBorder bg-white p-5">
                        <h3 className="font-serif text-lg">Révisions calibrées (SRS)</h3>
                        <p className="text-sm text-inkSoft mt-1">
                            Chaque mot que vous rencontrez rentre dans un système de répétition espacée. Vous ne révisez
                            que ce qui est <em>utile</em> à cet instant précis pour ancrer la mémoire.
                        </p>
                    </div>
                    <div className="rounded-2xl border border-lexiBorder bg-white p-5">
                        <h3 className="font-serif text-lg">Conversation IA guidée</h3>
                        <p className="text-sm text-inkSoft mt-1">
                            Des dialogues thématiques avec bilan structuré à la fin : ce qui coince, ce qui s&apos;améliore,
                            les phrases modèles à retenir.
                        </p>
                    </div>
                    <div className="rounded-2xl border border-lexiBorder bg-white p-5">
                        <h3 className="font-serif text-lg">Lexia — coach vocale temps réel</h3>
                        <p className="text-sm text-inkSoft mt-1">
                            Cliquez sur la bulle en bas à droite pour parler à Lexia. Elle vous corrige, ouvre vos
                            révisions à la voix, traduit une phrase, ou simplement discute avec vous.
                        </p>
                    </div>
                    <div className="rounded-2xl border border-lexiBorder bg-white p-5">
                        <h3 className="font-serif text-lg">Podcasts et quiz d&apos;écoute</h3>
                        <p className="text-sm text-inkSoft mt-1">
                            Des podcasts courts générés sur mesure autour de vos mots du moment, suivis d&apos;un quiz de
                            compréhension pour valider l&apos;acquis.
                        </p>
                    </div>
                    <div className="rounded-2xl border border-lexiBorder bg-white p-5">
                        <h3 className="font-serif text-lg">Mode intensif</h3>
                        <p className="text-sm text-inkSoft mt-1">
                            Pour les objectifs pressés : 50 ou 100 mots par jour, en blocs courts, avec un test global
                            en fin de session.
                        </p>
                    </div>
                    <div className="rounded-2xl border border-lexiBorder bg-white p-5">
                        <h3 className="font-serif text-lg">Parcours professionnels</h3>
                        <p className="text-sm text-inkSoft mt-1">
                            Anglais médical, anglais des affaires, tourisme, hôtellerie… Des parcours métier avec
                            modules dédiés, vocabulaire ciblé et mises en situation.
                        </p>
                    </div>
                    <div className="rounded-2xl border border-lexiBorder bg-white p-5">
                        <h3 className="font-serif text-lg">Certificats de complétion</h3>
                        <p className="text-sm text-inkSoft mt-1">
                            À la fin d&apos;un parcours pro, vous recevez un certificat téléchargeable en PNG / PDF avec
                            un lien public de vérification. Non certifiant CECRL — c&apos;est une reconnaissance de
                            complétion, pas un diplôme.
                        </p>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="space-y-4">
                <Anchor id="faq" kicker="Bonus" title="Questions fréquentes" />
                <details className="rounded-2xl border border-lexiBorder bg-white p-4 open:bg-paperAlt" data-testid="faq-micro">
                    <summary className="cursor-pointer font-medium">Est-ce que je dois autoriser mon micro ?</summary>
                    <p className="mt-2 text-sm text-inkSoft">
                        Uniquement pour les exercices de prononciation et les conversations vocales avec Lexia. Vous
                        pouvez refuser l&apos;accès au micro et continuer à utiliser Lexia School — vous perdrez
                        seulement l&apos;analyse de prononciation et l&apos;assistante temps réel.
                    </p>
                </details>
                <details className="rounded-2xl border border-lexiBorder bg-white p-4 open:bg-paperAlt" data-testid="faq-voix">
                    <summary className="cursor-pointer font-medium">Puis-je choisir la voix de Lexia ?</summary>
                    <p className="mt-2 text-sm text-inkSoft">
                        Oui — six voix sont disponibles (alloy, echo, fable, onyx, nova, shimmer). Vous les
                        sélectionnez depuis Réglages → Profil. La voix par défaut est <em>shimmer</em>.
                    </p>
                </details>
                <details className="rounded-2xl border border-lexiBorder bg-white p-4 open:bg-paperAlt" data-testid="faq-langues">
                    <summary className="cursor-pointer font-medium">Quelles langues sont disponibles ?</summary>
                    <p className="mt-2 text-sm text-inkSoft">
                        Une soixantaine de langues, avec une qualité meilleure sur les principales langues occidentales
                        (anglais, espagnol, italien, allemand, portugais). Pour les langues à écriture non latine
                        (arabe, japonais, russe, chinois…), la qualité est bonne mais la prononciation TTS peut varier.
                    </p>
                </details>
                <details className="rounded-2xl border border-lexiBorder bg-white p-4 open:bg-paperAlt" data-testid="faq-cecrl">
                    <summary className="cursor-pointer font-medium">Le niveau CECRL est-il officiel ?</summary>
                    <p className="mt-2 text-sm text-inkSoft">
                        Non. Le niveau affiché sur votre tableau de bord et sur nos certificats est une <strong>estimation</strong>{" "}
                        basée sur le test de placement et votre progression. Ce n&apos;est pas une certification officielle
                        au sens du Cadre européen commun de référence pour les langues.
                    </p>
                </details>
                <details className="rounded-2xl border border-lexiBorder bg-white p-4 open:bg-paperAlt" data-testid="faq-donnees">
                    <summary className="cursor-pointer font-medium">Que fait-on de mes données audio ?</summary>
                    <p className="mt-2 text-sm text-inkSoft">
                        Vos enregistrements de prononciation sont envoyés à OpenAI (Whisper) pour transcription, puis
                        au modèle d&apos;analyse. Ils ne sont pas conservés au-delà du traitement. Voir la{" "}
                        <a href="/confidentialite" className="text-terracotta hover:underline">politique de confidentialité</a>{" "}
                        pour le détail.
                    </p>
                </details>
            </section>
        </LegalShell>
    );
}
